import React from 'react';
import { Image, TouchableOpacity, View } from 'react-native';

import { getProductHealthComplianceLocalValue } from '../../../common/utils/products/healthCompliance';
import { getProductImageUrl } from '../../../common/utils/products/productImageUrlResolver';
import { formatCurrency } from '../../../common/utils/utils';
import { Text } from '../../components';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { ProductHealthCompliance } from '../product';
import { resolveQuantityLabel, showProductNameToast } from './PointOfSalePlanogramHelpers';

// One machine selection in the grid view.
const PointOfSalePlanogramGridCell = ({
	item,
	gridCellWidth,
	isLastInRow,
	cellHeight,
	tenant,
	healthComplianceLabel,
}) => {
	const styles = useThemedStyles(createStyles);
	const selectionLabel = item.selection || `${item.row}${item.column}`;
	const quantityLabel = resolveQuantityLabel(item);
	const healthComplianceValue = tenant ? getProductHealthComplianceLocalValue(item.product, tenant) : null;
	const priceLabel = formatCurrency(item.price) || '$0';
	const showCategoryText = gridCellWidth >= 90;

	return (
		<View
			style={[
				styles.gridCell,
				{
					width: gridCellWidth,
					minHeight: cellHeight,
				},
				isLastInRow && styles.gridCellLastInRow,
			]}
		>
			<View style={styles.gridCellHeader}>
				<Text style={styles.gridSelection}>{selectionLabel}</Text>
				<View style={styles.gridHealthContainer}>
					<View style={styles.gridHealthBadge}>
						<ProductHealthCompliance product={item.product} />
					</View>
					{
						showCategoryText && Boolean(healthComplianceValue) && (
							<Text style={styles.gridHealthText}>
								{`${healthComplianceLabel}: ${healthComplianceValue}`}
							</Text>
						)
					}
				</View>
			</View>
			<TouchableOpacity
				onPress={() => showProductNameToast(item)}
				activeOpacity={0.7}
				style={styles.gridProductNamePressArea}
			>
				{
					item.product && (
						<Image
							style={styles.gridProductImage}
							source={{ uri: getProductImageUrl(item.product) }}
						/>
					)
				}
				<Text style={styles.gridProductName} numberOfLines={1} ellipsizeMode="tail">
					{item.product?.name || 'Empty'}
				</Text>
			</TouchableOpacity>
			<View style={styles.gridFooter}>
				<Text style={styles.gridQuantity}>{quantityLabel}</Text>
				<Text style={styles.gridPrice}>{priceLabel}</Text>
			</View>
		</View>
	);
};

const createStyles = ({ ui: { colors, type, space, radius } }) => ({
	gridCell: {
		borderWidth: 1,
		borderColor: colors.raisedBorder,
		borderRadius: radius.tag,
		paddingHorizontal: space.xs,
		paddingTop: space.xs,
		backgroundColor: colors.raised,
		marginRight: 4,
		overflow: 'hidden',
	},
	gridCellLastInRow: {
		marginRight: 0,
	},
	gridCellEmpty: {
		backgroundColor: colors.card,
	},
	gridCellHeader: {
		flexDirection: 'row',
		justifyContent: 'space-between',
		alignItems: 'center',
		gap: 2,
	},
	gridSelection: {
		...type.label,
		color: colors.text,
		flexShrink: 1,
	},
	gridHealthContainer: {
		flexDirection: 'row',
		alignItems: 'center',
	},
	gridHealthBadge: {
		flexShrink: 0,
	},
	gridHealthText: {
		...type.caption,
		color: colors.textSecondary,
		marginLeft: 2,
	},
	gridQuantity: {
		...type.caption,
		color: colors.textSecondary,
	},
	gridProductImage: {
		width: 28,
		height: 28,
		marginTop: space.xs,
		marginBottom: 2,
		alignSelf: 'center',
		resizeMode: 'contain',
	},
	gridProductName: {
		...type.caption,
		color: colors.text,
		flexShrink: 1,
	},
	gridProductNamePressArea: {
		marginBottom: space.xs,
	},
	// Quantity above price so each gets the full cell width at 12 pt.
	gridFooter: {
		marginTop: 'auto',
		marginLeft: -space.xs,
		marginRight: -space.xs,
		paddingHorizontal: space.xs,
		paddingVertical: 2,
		backgroundColor: colors.card,
		borderTopWidth: 1,
		borderTopColor: colors.border,
	},
	gridPrice: {
		...type.label,
		color: colors.text,
	},
});

export default PointOfSalePlanogramGridCell;
